import { Language } from './i18n';

/**
 * Normalizes digits in exercise reps/rest strings according to language settings.
 */
export function normalizeExerciseDigits(str: string | undefined | null, lang: Language): string {
  if (!str) return '';
  let res = String(str);
  if (lang === 'fa') {
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    return res.replace(/[0-9]/g, (w) => persianDigits[parseInt(w, 10)]);
  } else {
    const persianDigitsMap: Record<string, string> = {
      '۰': '0', '۱': '1', '۲': '2', '۳': '3', '۴': '4',
      '۵': '5', '۶': '6', '۷': '7', '۸': '8', '۹': '9'
    };
    return res.replace(/[۰-۹]/g, (w) => persianDigitsMap[w] || w);
  }
}

/**
 * Localizes difficulty level for UI display label
 */
export function getLocalizedDifficulty(diff: string | undefined | null, lang: Language): string {
  if (!diff) return lang === 'fa' ? 'مبتدی' : lang === 'sv' ? 'Nybörjare' : 'Beginner';
  const val = diff.toLowerCase();
  if (val.includes('beg') || val.includes('مبتدی') || val.includes('سبک') || val.includes('lätt') || val.includes('nybörjare')) {
    return lang === 'fa' ? 'مبتدی' : lang === 'sv' ? 'Nybörjare' : 'Beginner';
  }
  if (val.includes('inter') || val.includes('متوسط') || val.includes('استاندارد') || val.includes('medel')) {
    return lang === 'fa' ? 'متوسط' : lang === 'sv' ? 'Medel' : 'Intermediate';
  }
  if (val.includes('adv') || val.includes('پیشرفته') || val.includes('تخصصی') || val.includes('avancerad')) {
    return lang === 'fa' ? 'پیشرفته' : lang === 'sv' ? 'Avancerad' : 'Advanced';
  }
  return diff;
}

/**
 * Localizes category label for UI display
 */
export function getLocalizedCategory(cat: string | undefined | null, lang: Language): string {
  if (!cat) return lang === 'fa' ? 'فیزیوتراپی و اصلاحی' : lang === 'sv' ? 'Fysioterapi & Rehab' : 'Physiotherapy & Rehab';
  const val = cat.toLowerCase();
  if (val.includes('rehab') || val.includes('فیزیوتراپی') || val.includes('اصلاحی')) {
    return lang === 'fa' ? 'فیزیوتراپی و اصلاحی' : lang === 'sv' ? 'Fysioterapi & Rehab' : 'Physiotherapy & Rehab';
  }
  if (val.includes('cardio') || val.includes('fat-loss') || val.includes('هوازی') || val.includes('کاردیو')) {
    return lang === 'fa' ? 'کاردیو و هوازی' : lang === 'sv' ? 'Kondition & Kardio' : 'Cardio & Aerobic';
  }
  if (val.includes('strength') || val.includes('قدرتی')) {
    return lang === 'fa' ? 'قدرتی و استقامتی' : lang === 'sv' ? 'Styrka & Uthållighet' : 'Strength & Conditioning';
  }
  if (val.includes('mobility') || val.includes('کششی') || val.includes('تحرک')) {
    return lang === 'fa' ? 'تحرک‌پذیری و کششی' : lang === 'sv' ? 'Rörlighet & Stretch' : 'Mobility & Stretching';
  }
  return cat;
}

/**
 * Audio / TTS phrases for guided workouts in each language
 */
export function getGuidedWorkoutPhrases(lang: Language = 'fa') {
  if (lang === 'sv') {
    return {
      voiceLang: 'sv-SE',
      prepare: (name: string) => `Gör dig redo för övning: ${name}`,
      exercise: () => 'Börja övningen nu!',
      restNext: (nextName: string) => `Vila nu. Nästa övning: ${nextName}`,
      restEnd: () => 'Vila nu. Passet är slutfört.',
      complete: () => 'Strålande jobbat! Dagens träningspass är framgångsrikt slutfört.'
    };
  }
  
  if (lang === 'en') {
    return {
      voiceLang: 'en-US',
      prepare: (name: string) => `Get ready for exercise: ${name}`,
      exercise: () => 'Start the exercise now!',
      restNext: (nextName: string) => `Rest now. Next exercise: ${nextName}`,
      restEnd: () => 'Rest now. Workout is finished.',
      complete: () => 'Great job! Today\'s session is successfully completed.'
    };
  }

  // Persian (Default)
  return {
    voiceLang: 'fa-IR',
    prepare: (name: string) => `آماده شوید برای تمرین: ${name}`,
    exercise: () => 'حرکت را شروع کنید!',
    restNext: (nextName: string) => `استراحت کنید. تمرین بعدی: ${nextName}`,
    restEnd: () => 'استراحت کنید. تمرین به پایان رسید.',
    complete: () => 'عالی بود! تمرین امروز شما با موفقیت تکمیل شد.'
  };
}
